import cn from "classnames"

import style from "./style.module.scss"

import { Text } from "ui-kit/components-branded"
import { AlterGuaranties } from "modules/Booking/components/AlterGuaranties"
import { UserAccountData } from "utils/interfaces"
import { NextPageButton } from "modules/Booking/FormPage/components/NextPageButton"
import { TimeRequestInfo } from "modules/Booking/FormPage/components/TimeRequestInfo"
import { PriceEmoji } from "modules/Booking/FormPage/components/PriceEmoji"
import { useBookingContext } from "modules/Booking/BookingContext"
import { useGetServicePrice } from "modules/Booking/common/hooks"
import { useOnlyFreeSessions } from "pages/FreePsysPage/useOnlyFreeSessions"
import { OnlineOrOfflineLabel } from "modules/BookingCommon/components"
import { getSingleOrFamilyText } from "modules/BookingCommon/common/utils"
import { SESSION_TYPE, TIME_REQUEST_TIME_DURATION } from "modules/Booking/common/constants"
import { Icon, Price, Discount } from "components"
import { formatToTotal } from "utils/datetime-format-utils"
import { getValidateRequestTimeSelector } from "modules/Booking/common/utils"
import { useShowPrice } from "utils/hooks/useShowPrice"
import { renderCityWithDetails } from "utils/helpers/psychologistUtil"

interface Props {
    formHasErrors: boolean
    showErrors(): void
    userData: UserAccountData
}

export function SessionInfoCard({ userData, formHasErrors, showErrors }: Props) {
    const [
        {
            serviceGroup,
            psy,
            selectedTime,
            slotDuration,
            selectedDiscount,
            currency,
            sessionType,
            requestTimesSelectors,
            isOnline,
            hasPreviousSessionsWithSamePsychologist,
            selectDeposit,
        },
    ] = useBookingContext()
    const [price, discountPrice] = useGetServicePrice()
    const showPrice = useShowPrice()
    const onlyFree = useOnlyFreeSessions()

    return (
        <div className={style.container} data-testid="session-info">
            <div className={style.content}>
                <div className={style.headline}>Информация о&nbsp;сессии</div>
                <div className={style.person}>
                    <OnlineOrOfflineLabel online={isOnline} />
                    <span className={style.bullet}>•</span>
                    <span data-testid="session-type">
                        {getSingleOrFamilyText(!serviceGroup?.family)}
                    </span>
                </div>
                {sessionType !== SESSION_TYPE.TIME_REQUEST && (
                    <>
                        <Text className="is-block mgb-4" data-testid="session-time">
                            {formatToTotal(new Date(selectedTime))}
                        </Text>
                        {!hasPreviousSessionsWithSamePsychologist && (
                            <Text className="is-block mgb-4">
                                Длительность {slotDuration} минут
                            </Text>
                        )}
                    </>
                )}
                {sessionType === SESSION_TYPE.TIME_REQUEST &&
                    getValidateRequestTimeSelector(requestTimesSelectors) && (
                        <>
                            <TimeRequestInfo requestTimesSelectors={requestTimesSelectors} />
                            {!hasPreviousSessionsWithSamePsychologist && (
                                <Text className="mgb-4 is-block">{TIME_REQUEST_TIME_DURATION}</Text>
                            )}
                        </>
                    )}
                {!isOnline && !hasPreviousSessionsWithSamePsychologist && (
                    <div className={style.location}>
                        <Icon type="nav" color="blue-3" size={24} className={style.locationIcon} />
                        {renderCityWithDetails(psy.new_city.name, psy.city_details)}
                    </div>
                )}
                {showPrice && (
                    <div className="booking-form__session-info__price-container">
                        <div
                            className={cn("booking-cards__session-info-price", {
                                isDeposit: sessionType === SESSION_TYPE.DEPOSIT,
                            })}
                        >
                            {onlyFree ? (
                                "Бесплатно"
                            ) : (
                                <>
                                    {sessionType !== SESSION_TYPE.DEPOSIT && (
                                        <>
                                            <PriceEmoji />
                                            Стоимость&nbsp;
                                        </>
                                    )}
                                    {sessionType === SESSION_TYPE.DEPOSIT && (
                                        <div className="is-flex is-align-items-center mb-2 w-100">
                                            <PriceEmoji />
                                            Стоимость абонемента
                                        </div>
                                    )}
                                    <Price
                                        psy={psy}
                                        singleServicePrice={discountPrice}
                                        className="is-lowercase"
                                        currency={currency}
                                        deposit={
                                            sessionType === SESSION_TYPE.DEPOSIT
                                                ? selectDeposit
                                                : undefined
                                        }
                                        depositWithDiscount
                                    />
                                    <Discount
                                        singleServicePrice={
                                            selectedDiscount ? price : discountPrice
                                        }
                                        psy={psy}
                                        className="booking-cards__session-info-price-discount"
                                        showDiscountInBooking={
                                            Boolean(selectedDiscount) && price > discountPrice
                                        }
                                        currency={currency}
                                        deposit={
                                            sessionType === SESSION_TYPE.DEPOSIT
                                                ? selectDeposit
                                                : undefined
                                        }
                                    />
                                </>
                            )}
                        </div>
                    </div>
                )}
                <NextPageButton
                    formHasErrors={formHasErrors}
                    showErrors={showErrors}
                    userData={userData}
                />
            </div>
            <AlterGuaranties />
        </div>
    )
}
